import { CSSResult, html, LitElement, TemplateResult, unsafeCSS } from 'lit';
import { state } from 'lit/decorators.js';
import { provide } from '@lit/context';
import { Messages, OnlineReservationConfig, Store } from '../../@types/online-reservation.js';

import '../tooltip/tooltip.js';

import { sharedCss } from '../../scripts/style/shared.js';
import styles from './online-reservation.scss?inline';
import { i18nContext } from './i18nContext.js';
import { AppState, ModelState, SelectionState } from './states/index.js';
import arrowBackIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_arrow-left.svg?element';
import { addPresentationToIcon } from '../icon/icon-accessibility.js';
import { DomainEvent } from '@cube/tracking-library-api';
import { OnlineReservationResponseDTO } from './states/model-state.js';
import { ORInformation } from './content/or-information.js';
import { ORSize } from './content/or-size.js';
import { ORStore } from './content/or-store.js';
import {
  createInitialStepFourState,
  ORForm,
  ORFormParams,
  StepFourState,
  validateEmailValue,
} from './content/or-form.js';
import { ORResult } from './content/or-result.js';

export abstract class OnlineReservationBase extends LitElement {
  @provide({ context: i18nContext })
  protected messages!: Messages;

  @state() protected appState = new AppState();
  @state() protected modelState = new ModelState();
  @state() protected selectionState = new SelectionState();
  @state() protected stepFourState: StepFourState = createInitialStepFourState();
  @state() protected onboardingSkipped = false;

  protected config: OnlineReservationConfig = {
    articleDetailsUrl: '',
    messages: {},
    dataProtectionRegulationLink: '',
    reservationEndpoint: '',
    isNativeDialogEnabled: false,
  };

  private static readonly ONBOARDING_STORAGE_KEY = 'ents-or-onboarding-completed';

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  constructor() {
    super();
    this.messages = this.config.messages;
  }

  // Abstract methods that must be implemented by subclasses
  abstract handleClose(): void;

  abstract handleSuccessfulReservation(): void;

  abstract render(): TemplateResult;

  protected loadModel(variantId: string) {
    this.appState = this.appState.setLoading();

    const articleDetailsUrl = this.config.articleDetailsUrl.replace('VARIANT_ID', variantId);
    fetch(articleDetailsUrl)
      .then((response) => response.json())
      .then(this.handleModelLoadSuccess.bind(this))
      .catch(() => {
        this.appState = this.appState.setLoadedWithError();
      });
  }

  protected handleModelLoadSuccess(response: OnlineReservationResponseDTO) {
    this.modelState = ModelState.createFromResponse(response);
    this.appState = this.appState.setLoaded();
    this.onboardingSkipped = globalThis.localStorage.getItem(OnlineReservationBase.ONBOARDING_STORAGE_KEY) === 'true';

    if (this.onboardingSkipped && this.appState.currentStep === 0) {
      if (this.modelState.shouldSkipToStoreSelection()) {
        this.appState = this.appState.goToStoreSelectionStep();
      } else {
        this.appState = this.appState.nextStep();
      }
    }

    if (this.modelState.shouldSkipToStoreSelection()) {
      const firstSizeId = this.modelState.getFirstAvailableSize();
      if (firstSizeId) {
        this.selectionState = this.selectionState.selectSize(firstSizeId);
      }
    }
  }

  protected renderContent() {
    const currentStep = this.appState.currentStep;
    const isStepInvalid = currentStep === -1;

    if (this.appState.loadingState.isFailed) {
      return this.renderError();
    }

    if (isStepInvalid) {
      return '';
    }

    return html`
      <div slot="header-start">${this.renderBackButton()}</div>
      ${this.renderStep()}
    `;
  }

  private isFirstVisibleStepAfterSkippedOnboarding(): boolean {
    const { currentStep } = this.appState;
    const { skipSizeLayer } = this.modelState;

    return this.onboardingSkipped && ((currentStep === 1 && !skipSizeLayer) || (currentStep === 2 && skipSizeLayer));
  }

  protected renderBackButton() {
    if (
      this.appState.currentStep === 0 ||
      this.appState.currentStep === 4 ||
      this.isFirstVisibleStepAfterSkippedOnboarding()
    ) {
      return '';
    }

    return html`
      <a
        slot="header-start"
        class="bdk-link-secondary"
        size="medium"
        href="#"
        data-test-ents-or-back-button
        aria-label="${this.messages['back']}"
        @click="${this.handleBackNavigation}"
      >
        <div>${addPresentationToIcon(arrowBackIcon())}</div>
        ${this.messages['back']}
      </a>
    `;
  }

  protected readonly handleBackNavigation = () => {
    const { skipSizeLayer } = this.modelState;
    if (this.appState.currentStep === 2 && skipSizeLayer) {
      // Without size selection, go directly to the first step
      this.appState = this.appState.goToFirstStep();
    } else {
      // Otherwise just go back one step
      this.appState = this.appState.prevStep();
    }
  };

  protected renderError() {
    return html`
      <div class="ents-or__error">
        <p class="ents-copy ents-or__hint ents-or__hint--error">${this.config.messages.error}</p>
      </div>
    `;
  }

  protected renderStep() {
    const { currentStep } = this.appState;

    switch (currentStep) {
      case 0:
        return this.renderStepOne();
      case 1:
        return this.renderStepTwo();
      case 2:
        return this.renderStepThree();
      case 3:
        return this.renderStepFour();
      case 4:
        return this.renderStepFive();
      default:
        return '';
    }
  }

  protected renderStepOne() {
    const { article, skipSizeLayer } = this.modelState;

    const orInformation = new ORInformation(
      this.messages,
      () => {
        globalThis.localStorage.setItem(OnlineReservationBase.ONBOARDING_STORAGE_KEY, 'true');

        if (skipSizeLayer) {
          this.appState = this.appState.goToStoreSelectionStep();
        } else {
          this.appState = this.appState.nextStep();
        }
      },
      article,
      skipSizeLayer,
    );

    return orInformation.renderStep();
  }

  protected renderStepTwo() {
    const { sizesWithStoreStock } = this.modelState;

    const orSize = new ORSize(
      this.messages,
      () => (this.appState = this.appState.nextStep()),
      (articleId: string) => (this.selectionState = this.selectionState.selectSize(articleId)),
      sizesWithStoreStock,
    );

    return orSize.renderStep();
  }

  protected renderStepThree() {
    const { selectedSize } = this.selectionState;
    const { skipSizeLayer } = this.modelState;

    const orStore = new ORStore(
      this.messages,
      () => (this.appState = this.appState.nextStep()),
      (store: Store) => {
        this.selectionState = this.selectionState.selectStore(store);
        this.selectionState.saveSelectedStoreId();
      },
      this.modelState.getSizeById(selectedSize),
      !skipSizeLayer,
      this.modelState.getStoresForSize(selectedSize),
    );

    return orStore.renderStep();
  }

  protected renderStepFour() {
    const { selectedSize, selectedStore } = this.selectionState;
    const { skipSizeLayer } = this.modelState;
    const size = this.modelState.getSizeById(selectedSize);
    const config = {
      articleId: selectedSize,
      size: size,
      storeLocation: selectedStore,
      dataProtectionRegulationLink: this.config.dataProtectionRegulationLink,
      reservationEndpoint: this.config.reservationEndpoint,
    };
    const params: ORFormParams = {
      handleInputChange: (e) => this.handleInputChange(e),
      handleRadioChange: (salutation) => this.handleRadioChange(salutation),
      validateField: (e) => this.validateField(e),
      validateEmail: (e) => this.validateEmail(e),
      submitReservation: (e) => this.submitReservation(e),
    };

    const orForm = new ORForm(this.messages, config, this.stepFourState, !skipSizeLayer, params);

    return orForm.renderStep();
  }

  protected renderStepFive() {
    const { reservationSuccessful } = this.appState;
    const { selectedSize, selectedStore } = this.selectionState;
    const { article, skipSizeLayer } = this.modelState;
    const size = this.modelState.getSizeById(selectedSize);

    const orResult = new ORResult(
      this.messages,
      () => this.handleClose(),
      () => this.handleReservationRetry(),
      {
        reservationSuccessful: reservationSuccessful === true,
        article: article,
        storeLocationName: selectedStore.name,
        sizeDescription: size,
        showSizeBadge: !skipSizeLayer,
      },
    );

    return orResult.renderStep();
  }

  protected resetState() {
    this.appState = AppState.reset();
    this.selectionState = SelectionState.reset();
    this.stepFourState = createInitialStepFourState();
  }

  protected getTrackingData(): DomainEvent {
    return {
      event_name: 'click_reserve',
      event_data: {
        reservation_store_name: this.selectionState.selectedStore?.name || '',
      },
      currency: {
        code: 'EUR',
      },
      items: [
        {
          produkt_id: this.modelState.trackingData.produkt_id,
          farb_id: this.modelState.trackingData.farb_id ?? '',
          artikel_id: this.selectionState.selectedSize,
          vertriebsinfo_id: this.modelState.getsalesInformationIdById(this.selectionState.selectedSize),
          syan: this.modelState.trackingData.syan,
          quantity: 1,
        },
      ],
      responsible_team_tracking: 'ENTS',
      responsible_team_feature: 'ENTS',
    };
  }

  protected handleFailedReservation(errorMessage: string) {
    console.error(errorMessage);
    this.appState = this.appState.setReservationStatus(false);
    this.appState = this.appState.nextStep();
  }

  protected handleReservationRetry() {
    this.appState = this.appState.prevStep();
  }

  // eslint-disable-next-line class-methods-use-this
  private extractBdkFieldValue(target: HTMLElement): { name: string; value: string | boolean } | null {
    if (target.tagName === 'BDK-ENTS-TEXT-FIELD') {
      const bdkField = target as HTMLInputElement;
      return {
        name: bdkField.getAttribute('name') ?? '',
        value: bdkField.value,
      };
    }

    if (target.tagName === 'BDK-ENTS-CHECKBOX') {
      const bdkCheckbox = target as HTMLInputElement;
      return {
        name: bdkCheckbox.name ?? '',
        value: bdkCheckbox.checked,
      };
    }

    return null;
  }

  private handleInputChange(event: Event) {
    const target = event.target as HTMLElement;
    const fieldData = this.extractBdkFieldValue(target);

    if (!fieldData || !fieldData.name) {
      return;
    }

    const { name, value } = fieldData;

    const formData = {
      ...this.stepFourState.formData,
      [name]: value,
    };

    // Clear error when user starts typing
    let fieldErrors = { ...this.stepFourState.fieldErrors };
    if (fieldErrors[name]) {
      fieldErrors = {
        ...fieldErrors,
        [name]: false,
      };
    }

    this.stepFourState = {
      formData,
      fieldErrors,
    };
  }

  private handleRadioChange(salutation: 'ms' | 'mr' | 'diverse') {
    this.stepFourState = {
      ...this.stepFourState,
      formData: {
        ...this.stepFourState.formData,
        salutation,
      },
    };
  }

  private validateField(event: Event) {
    const target = event.target as HTMLElement;
    const fieldData = this.extractBdkFieldValue(target);

    const name = fieldData?.name ?? '';
    const value = typeof fieldData?.value === 'string' ? fieldData.value : '';

    if (name in this.stepFourState.fieldErrors) {
      const isValid = value.trim() !== '';

      this.stepFourState = {
        ...this.stepFourState,
        fieldErrors: {
          ...this.stepFourState.fieldErrors,
          [name]: !isValid,
        },
      };
    }
  }

  private validateEmail(event: Event) {
    const target = event.target as HTMLElement;
    let value = '';

    const fieldData = this.extractBdkFieldValue(target);
    if (fieldData && typeof fieldData.value === 'string') {
      value = fieldData.value;
    } else {
      const input = target as HTMLInputElement;
      if (input.value !== undefined) {
        value = input.value;
      } else {
        const bdkTextFieldHost = (input.getRootNode() as ShadowRoot)?.host;
        if (bdkTextFieldHost?.tagName === 'BDK-ENTS-TEXT-FIELD') {
          const bdkField = bdkTextFieldHost as HTMLInputElement;
          value = bdkField.value ?? '';
        }
      }
    }

    const isInvalid = validateEmailValue(value);

    this.stepFourState = {
      ...this.stepFourState,
      fieldErrors: {
        ...this.stepFourState.fieldErrors,
        email: isInvalid,
      },
    };
  }

  private validateAllFields(): boolean {
    const { formData } = this.stepFourState;

    // Make sure to call the validateEmailValue function correctly
    const newErrors = {
      firstName: formData.firstName.trim() === '',
      lastName: formData.lastName.trim() === '',
      email: validateEmailValue(formData.email),
    };

    this.stepFourState = {
      ...this.stepFourState,
      fieldErrors: newErrors,
    };

    return !Object.values(newErrors).some(Boolean);
  }

  // eslint-disable-next-line class-methods-use-this
  private setButtonDisabled(event: Event, disabled: boolean) {
    const btn = event.target as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = disabled;
    }
  }

  private submitReservation(event: Event) {
    const isValid = this.validateAllFields();

    if (isValid) {
      this.setButtonDisabled(event, true);

      const { formData } = this.stepFourState;
      const salutationMap: Record<string, string> = { ms: 'w', mr: 'm', diverse: 'd' };
      const apiRequestData = {
        artikelListe: [
          {
            artikelNummer: this.selectionState.selectedSize,
          },
        ],
        haus: {
          nummer: this.selectionState.selectedStore.storeId,
        },
        kundenKontakt: {
          geschlecht: salutationMap[formData.salutation],
          vorname: formData.firstName,
          nachname: formData.lastName,
          email: formData.email,
        },
        newsletteranmeldung: {
          newsletteranmeldung: formData.newsletter,
          labelText: this.messages.newsletter,
        },
      };

      fetch(this.config.reservationEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiRequestData),
      })
        .then((response) => {
          if (response.ok) {
            this.handleSuccessfulReservation();
          } else {
            this.handleFailedReservation('online-reservation request failed!');
          }
        })
        .catch((error) => {
          console.error('Reservation request failed:', error);
          this.handleFailedReservation('');
        })
        .finally(() => {
          this.setButtonDisabled(event, false);
        });
    }
  }

  protected updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('appState') || changedProps.has('onboardingSkipped') || changedProps.has('modelState')) {
      const prev = changedProps.get('appState') as AppState | undefined;

      if (!prev || prev.currentStep !== this.appState.currentStep) {
        this.focusStepTitle();
      }
      const isFirstVisibleStepAfterSkippedOnboarding = this.isFirstVisibleStepAfterSkippedOnboarding();
      this.toggleAttribute('data-hide-back-button', isFirstVisibleStepAfterSkippedOnboarding);
    }
  }

  private focusStepTitle() {
    this.updateComplete.then(() => {
      const title = this.renderRoot?.querySelector('#or-step-title') as HTMLElement;
      title?.focus();
    });
  }
}
